/**
 * Phone-verify dialog — two steps (phone → SMS code).
 *
 * Opened from the account page (STORY-001-010) and the seller-application flow
 * (EPIC-006). Behaviour is unchanged by the 2026-08 rebuild:
 *   - Step 1: phone in E.164, defaulting to "+996 " for KG. Submit POSTs
 *     /auth/phone/start, which issues an SMS OTP.
 *   - Step 2: the shared 6-cell OtpInput. Submit POSTs /auth/phone/verify; a
 *     200 closes the dialog and hands the verified number to `onSuccess`.
 *   - `role="dialog"` + `aria-modal`, focus moved in on open, Tab cycled inside,
 *     Escape and backdrop click both close, and focus restored to whatever was
 *     focused before.
 *
 * Why a hand-rolled dialog rather than shadcn's Dialog primitive: the original
 * story's allowed-touch list excluded `components/ui/dialog.tsx`. Left as-is
 * deliberately — swapping the primitive is a behavioural change to a focus trap
 * that already works, and this pass is visual.
 *
 * ── What the rebuild changed ─────────────────────────────────────────────────
 *
 * The two steps are ruled slips, matching /verify-email so the same gesture
 * looks the same wherever it appears. Three concrete defects fixed on the way:
 *
 *   1. The scrim was `bg-black/40` — a hard-coded colour in a themed app. It is
 *      `--g-scrim` now, which is 0.42 warm-black in light and 0.6 true black in
 *      dark, i.e. correct in both instead of correct in one.
 *   2. The error was rendered as a sibling paragraph with no programmatic link
 *      to the field, so a screen reader announced it (role="alert") without
 *      ever associating it with the input. It is now the field's own error, via
 *      `aria-describedby`.
 *   3. The close button was a 24px hit area. It is 44px.
 */
import { Loader2, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { OtpInput } from "@/features/auth/components/OtpInput";
import {
  SLIP_INPUT_CLASS,
  Slip,
  SlipField,
} from "@/features/auth/components/slip";
import { OTP_LENGTH } from "@/features/auth/schemas";
import { ApiError, NetworkError, apiClient, AUTH_PATHS } from "@/lib/api-client";
import { ICON, ICON_STROKE } from "@/lib/icons";
import { notify } from "@/lib/notify";

export interface PhoneVerifyDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Called when the user dismisses the dialog (backdrop / Escape / close / cancel). */
  onClose: () => void;
  /** The currently-authenticated user's id; included in the request bodies. */
  userId: string;
  /**
   * Fires after the phone-verify endpoint returns 200. The caller decides what
   * to do next. The dialog closes itself just before this fires.
   */
  onSuccess: (phoneE164: string) => void;
  /** Initial phone value. Defaults to `+996 `. */
  initialPhone?: string;
}

interface PhoneStartResponse {
  phone_pending_verification: true;
  throttle_until: string;
  /** NON-PRODUCTION ONLY demo affordance (SCRUM-191(a)) — server-gated. */
  dev_otp?: string | null;
  /**
   * True iff the caller's own most recent prior SMS to this number is
   * recorded as ``failed`` (SCRUM-191(b)). Server-gated to the
   * requesting user's own history — see ``router_phone.
   * _read_own_prior_send_failed``.
   */
  last_send_failed?: boolean;
}

interface PhoneVerifyResponse {
  user_id: string;
  phone_verified: true;
}

function mapStartError(err: unknown): string {
  if (err instanceof NetworkError) return "errors.network";
  if (!(err instanceof ApiError)) return "errors.generic";
  switch (err.code) {
    case "auth.phone.invalid_format":
      return "errors.auth.phone.invalid_format";
    case "auth.phone.already_taken":
      return "errors.auth.phone.already_taken";
    case "auth.phone.user_id_mismatch":
      return "errors.auth.phone.user_id_mismatch";
    default:
      return "errors.generic";
  }
}

function mapVerifyError(err: unknown): string {
  if (err instanceof NetworkError) return "errors.network";
  if (!(err instanceof ApiError)) return "errors.generic";
  switch (err.code) {
    case "auth.otp.invalid":
      return "errors.auth.otp.invalid";
    case "auth.otp.expired":
      return "errors.auth.otp.expired";
    case "auth.otp.max_attempts":
      return "errors.auth.otp.max_attempts";
    case "auth.otp.destination_mismatch":
      return "errors.auth.otp.destination_mismatch";
    case "auth.phone.user_id_mismatch":
      return "errors.auth.phone.user_id_mismatch";
    default:
      return "errors.generic";
  }
}

export function PhoneVerifyDialog({
  open,
  onClose,
  userId,
  onSuccess,
  initialPhone = "+996 ",
}: PhoneVerifyDialogProps): ReactElement | null {
  const { t } = useTranslation("auth");
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [phone, setPhone] = useState(initialPhone);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  // SCRUM-191(a) — server-gated demo OTP hint (local/qa only; always
  // absent in prod).
  const [devOtp, setDevOtp] = useState<string | null>(null);
  // SCRUM-191(b) — the previous start/resend's reported delivery outcome.
  const [sendFailed, setSendFailed] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  // The wallclock (ISO8601, from the server) the resend button re-enables
  // at. Ticked down into ``resendSecondsLeft`` below.
  const [throttleUntil, setThrottleUntil] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Reset state every time the dialog opens.
  useEffect(() => {
    if (open) {
      setStep("phone");
      setPhone(initialPhone);
      setOtp("");
      setBusy(false);
      setErrorKey(null);
      setDevOtp(null);
      setSendFailed(false);
      setResendBusy(false);
      setThrottleUntil(null);
    }
  }, [open, initialPhone]);

  // Tick once a second while a throttle is active — drives the resend
  // countdown. Stops entirely once the throttle clears (no free-running
  // interval while the dialog just sits open).
  useEffect(() => {
    if (throttleUntil === null) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return (): void => window.clearInterval(id);
  }, [throttleUntil]);

  const resendSecondsLeft = useMemo(() => {
    if (throttleUntil === null) return 0;
    const target = new Date(throttleUntil).getTime();
    if (Number.isNaN(target)) return 0;
    return Math.max(0, Math.ceil((target - nowMs) / 1000));
  }, [throttleUntil, nowMs]);

  // Trap focus and handle Escape.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const root = dialogRef.current;
    if (root !== null) {
      // Move focus into the dialog on open.
      const first = root.querySelector<HTMLElement>(
        "input, button, select, textarea, [tabindex]:not([tabindex='-1'])",
      );
      first?.focus();
    }

    function handleKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === "Tab" && root !== null) {
        const focusables = Array.from(
          root.querySelectorAll<HTMLElement>(
            "input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
          ),
        );
        if (focusables.length === 0) return;
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", handleKey);
    return (): void => {
      document.removeEventListener("keydown", handleKey);
      previouslyFocused?.focus();
    };
  }, [open, onClose]);

  const sanitizedPhone = useCallback((raw: string): string => {
    // Keep + plus digits; collapse whitespace.
    return raw.replace(/[^+\d]/g, "");
  }, []);

  const onStartSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    setErrorKey(null);
    const phoneE164 = sanitizedPhone(phone);
    if (!/^\+\d{8,15}$/.test(phoneE164)) {
      setErrorKey("errors.auth.phone.invalid_format");
      return;
    }
    setBusy(true);
    try {
      const data = await apiClient.post<
        PhoneStartResponse,
        { user_id: string; phone_e164: string }
      >(AUTH_PATHS.phoneStart, { user_id: userId, phone_e164: phoneE164 });
      setPhone(phoneE164);
      setDevOtp(data.dev_otp ?? null);
      setSendFailed(data.last_send_failed === true);
      setThrottleUntil(data.throttle_until);
      setNowMs(Date.now());
      setStep("otp");
    } catch (err) {
      setErrorKey(mapStartError(err));
    } finally {
      setBusy(false);
    }
  };

  // SCRUM-191(b) — "Send again": re-POSTs /auth/phone/start for the same
  // number without leaving the OTP step. Disabled while the previous
  // start's throttle window hasn't elapsed (``resendSecondsLeft > 0``).
  const onResend = useCallback(async (): Promise<void> => {
    if (resendBusy || resendSecondsLeft > 0) return;
    setSendFailed(false);
    setResendBusy(true);
    try {
      const data = await apiClient.post<
        PhoneStartResponse,
        { user_id: string; phone_e164: string }
      >(AUTH_PATHS.phoneStart, {
        user_id: userId,
        phone_e164: sanitizedPhone(phone),
      });
      setDevOtp(data.dev_otp ?? null);
      setSendFailed(data.last_send_failed === true);
      setThrottleUntil(data.throttle_until);
      setNowMs(Date.now());
      setOtp("");
    } catch (err) {
      setErrorKey(mapStartError(err));
    } finally {
      setResendBusy(false);
    }
  }, [resendBusy, resendSecondsLeft, userId, phone, sanitizedPhone]);

  // Takes the code as an argument (defaulting to component state) rather
  // than reading `otp` only. OtpInput fires `onComplete(next)` in the SAME
  // synchronous handler as the `setOtp(next)` that precedes it, and React
  // batches that update -- so the `onVerifySubmit` closure invoked from
  // `onComplete` still captured the PREVIOUS render's `otp`, holding 5 of
  // the 6 digits. The length guard then returned early and
  // /auth/phone/verify was never called on auto-complete. Mirrors
  // VerifyEmailScreen's `onComplete={(full) => submitOtp(full)}`.
  const onVerifySubmit = useCallback(async (code?: string): Promise<void> => {
    const submitted = code ?? otp;
    if (submitted.length !== OTP_LENGTH) return;
    setErrorKey(null);
    setBusy(true);
    try {
      await apiClient.post<
        PhoneVerifyResponse,
        { user_id: string; otp: string; phone_e164: string }
      >(AUTH_PATHS.phoneVerify, {
        user_id: userId,
        otp: submitted,
        phone_e164: sanitizedPhone(phone),
      });
      notify.success(t("phoneVerify.successToast"));
      onClose();
      onSuccess(sanitizedPhone(phone));
    } catch (err) {
      setErrorKey(mapVerifyError(err));
      setOtp("");
    } finally {
      setBusy(false);
    }
  }, [otp, userId, phone, sanitizedPhone, t, onClose, onSuccess]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[var(--g-scrim)] px-4 py-6 sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="phone-verify-backdrop"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="phone-verify-title"
        className={
          "w-full max-w-sm rounded-[var(--g-radius-lg)] border border-[var(--g-rule)] " +
          "bg-[var(--g-elevated)] p-5 shadow-[var(--g-shadow-xl)]"
        }
        data-testid="phone-verify-dialog"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2
            id="phone-verify-title"
            className="font-display text-lg font-bold leading-tight text-[var(--g-ink)]"
          >
            {t("phoneVerify.title")}
          </h2>
          <button
            type="button"
            className={
              "-mr-2 -mt-1 grid size-11 shrink-0 place-items-center rounded-[var(--g-radius-sm)] " +
              "text-[var(--g-ink-2)] transition-colors hover:bg-[var(--g-sunken)] hover:text-[var(--g-ink)] " +
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            }
            onClick={onClose}
            aria-label={t("phoneVerify.close")}
            data-testid="phone-verify-close"
          >
            <X size={ICON.md} strokeWidth={ICON_STROKE} aria-hidden="true" />
          </button>
        </div>

        {step === "phone" ? (
          <form className="flex flex-col gap-4" onSubmit={onStartSubmit} noValidate>
            <Slip>
              <SlipField
                htmlFor="phone-verify-phone"
                label={t("phoneVerify.phoneLabel")}
                required
                requiredLabel={t("common.requiredField")}
                hint={t("phoneVerify.phoneHint")}
                hintId="phone-verify-phone-hint"
                error={errorKey !== null ? t(errorKey) : undefined}
                errorId="phone-verify-phone-error"
                errorTestId="phone-verify-error"
              >
                <input
                  id="phone-verify-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  aria-required="true"
                  aria-invalid={errorKey !== null || undefined}
                  aria-describedby={
                    errorKey !== null
                      ? "phone-verify-phone-error phone-verify-phone-hint"
                      : "phone-verify-phone-hint"
                  }
                  placeholder={t("phoneVerify.phonePlaceholder")}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={busy}
                  data-testid="phone-verify-phone"
                  className={SLIP_INPUT_CLASS}
                />
              </SlipField>
            </Slip>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={busy}
                className="h-11"
                data-testid="phone-verify-cancel"
              >
                {t("phoneVerify.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={busy}
                className="h-11"
                data-testid="phone-verify-send"
              >
                {busy ? (
                  <>
                    <Loader2
                      size={ICON.sm}
                      strokeWidth={ICON_STROKE}
                      className="g-spin"
                      aria-hidden="true"
                    />
                    <span>{t("phoneVerify.sending")}</span>
                  </>
                ) : (
                  t("phoneVerify.sendCode")
                )}
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-[var(--g-ink-2)]">
              {t("phoneVerify.otpLede")}{" "}
              <strong className="g-data font-medium text-[var(--g-figure)]">
                {sanitizedPhone(phone)}
              </strong>
            </p>

            {devOtp !== null && devOtp !== "" ? (
              <div
                role="note"
                data-testid="phone-verify-dev-hint"
                className="rounded-[var(--g-radius)] border border-[var(--g-rule)] bg-[var(--g-sunken)] px-3.5 py-3 text-[13px] leading-snug text-[var(--g-ink-2)]"
              >
                {t("phoneVerify.devHint")}{" "}
                <b className="g-data text-foreground tracking-[0.25em]">
                  {devOtp}
                </b>
              </div>
            ) : null}

            <Slip>
              <div className="px-4 py-3">
                <OtpInput
                  value={otp}
                  onChange={(next) => {
                    setOtp(next);
                    if (errorKey !== null) setErrorKey(null);
                  }}
                  onComplete={(full) => {
                    if (!busy) void onVerifySubmit(full);
                  }}
                  label={t("phoneVerify.otpLabel")}
                  disabled={busy}
                  invalid={errorKey !== null}
                  errorId={errorKey !== null ? "phone-verify-otp-error" : undefined}
                  testId="phone-verify-otp-cell"
                />
                {errorKey !== null ? (
                  <p
                    id="phone-verify-otp-error"
                    role="alert"
                    className="mt-2 text-[0.75rem] font-medium leading-snug text-[var(--g-error)]"
                    data-testid="phone-verify-error"
                  >
                    {t(errorKey)}
                  </p>
                ) : null}
              </div>
            </Slip>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-[var(--g-ink-2)]">
              <span>{t("phoneVerify.noCodeHint")}</span>
              {resendSecondsLeft > 0 ? (
                <span data-testid="phone-verify-resend-countdown">
                  {t("phoneVerify.resendIn", { seconds: resendSecondsLeft })}
                </span>
              ) : (
                <button
                  type="button"
                  data-testid="phone-verify-resend"
                  onClick={() => void onResend()}
                  disabled={resendBusy}
                  className="min-h-11 border-none bg-transparent text-[13px] font-bold text-primary underline underline-offset-4 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {t("phoneVerify.resend")}
                </button>
              )}
            </div>

            {sendFailed ? (
              <p
                role="alert"
                data-testid="phone-verify-send-failed"
                className="text-[0.75rem] font-medium leading-snug text-[var(--g-error)]"
              >
                {t("phoneVerify.sendFailed")}
              </p>
            ) : null}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep("phone")}
                disabled={busy}
                className="h-11"
                data-testid="phone-verify-back"
              >
                {t("phoneVerify.back")}
              </Button>
              <Button
                type="button"
                onClick={() => void onVerifySubmit()}
                disabled={busy || otp.length !== OTP_LENGTH}
                className="h-11"
                data-testid="phone-verify-verify"
              >
                {busy ? (
                  <>
                    <Loader2
                      size={ICON.sm}
                      strokeWidth={ICON_STROKE}
                      className="g-spin"
                      aria-hidden="true"
                    />
                    <span>{t("phoneVerify.verifying")}</span>
                  </>
                ) : (
                  t("phoneVerify.verify")
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PhoneVerifyDialog;
